import { FunctionComponent, useCallback } from "react";

import { useTranslation } from "react-i18next";

import { faCircleCheck } from "@fortawesome/pro-regular-svg-icons";

import { RESCRAPE_CREDITS } from "@signalist/shared-types";

import { Icon } from "@/components/Icon/Icon";
import { Modal } from "@/components";

import { useAppSelector } from "@/redux/hooks";
import { websiteScrapeSelector } from "@/pages/Private/redux/websiteScrape/websiteScrape.slice";

export interface RescrapeConfirmModalProps {
	isOpened: boolean;
	isLoading: boolean;
	handleClose: () => void;
	handleConfirm: () => void;
	handleBuyCredits: () => void;
}

/**
 * Confirms ordering another scrape of a source (PREC-3091): what it costs, and the promise that
 * leads already in the list are not charged again. With no credits left the confirm turns into a
 * "buy credits" path instead of failing on the server.
 */
export const RescrapeConfirmModal: FunctionComponent<RescrapeConfirmModalProps> = ({
	isOpened,
	isLoading,
	handleClose,
	handleConfirm,
	handleBuyCredits,
}) => {
	const { t } = useTranslation();
	const ts = useCallback(
		(key: string, options?: { amount?: number }) => t(`customEvent.rescrape.modal.${key}`, options),
		[t]
	);

	const { credits } = useAppSelector(websiteScrapeSelector);

	const hasEnoughCredits = credits >= RESCRAPE_CREDITS;

	return (
		<Modal
			handleClose={handleClose}
			handleSave={hasEnoughCredits ? handleConfirm : handleBuyCredits}
			isLoading={isLoading}
			isOpened={isOpened}
			size="sm"
			submitButtonText={hasEnoughCredits ? ts("confirm") : ts("buyCredits")}
			title={ts("title")}
		>
			<div className="flex flex-col gap-4">
				<div className="text-sm text-text-meta">{ts("description")}</div>

				<div className="flex flex-row items-center justify-between rounded-xl bg-background-light px-4 py-3">
					<div className="text-xs text-text-meta">{ts("cost")}</div>
					<div className="flex flex-col items-end">
						<div className="text-smd font-bold text-text">
							{ts("costValue", { amount: RESCRAPE_CREDITS })}
						</div>
						<div className="text-xs text-text-meta">{ts("balance", { amount: credits })}</div>
					</div>
				</div>

				{hasEnoughCredits ? (
					<div className="flex flex-row items-start gap-2 rounded-xl bg-status-success-light px-4 py-3 text-xs text-status-success">
						<Icon className="w-[14px] h-auto shrink-0 mt-0.5" icon={faCircleCheck} />
						<span>{ts("dedupNote")}</span>
					</div>
				) : (
					<div className="rounded-xl bg-status-running-light px-4 py-3 text-xs text-status-running">
						{ts("notEnoughCredits")}
					</div>
				)}
			</div>
		</Modal>
	);
};
